import { gql } from "@apollo/client";
import FormText from "controls/FormText";
import Message from "controls/Message";
import MultiValuesField from "controls/MultiValuesField";
import useData from "hooks/useData";
import DateTimeInput from "inputs/DateTimeInput";
import DurationInput from "inputs/DurationInput";
import SwitchInput from "inputs/SwitchInput";
import React, { useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

const GET_LEAGUE_TEAMS = gql`
  query GetLeagueTeams($id: ID!) {
    league(id: $id) {
      id
      teams {
        id
        name
      }
    }
  }
`;

export const GameSchedulesFormFragment = gql`
  fragment GameSchedulesFormFragment on GameSchedule {
    court {
      id
    }
    league {
      id
    }
    homeTeam {
      id
    }
    awayTeam {
      id
    }
    org {
      id
    }
    scheduledAt
    durationSeconds
    cancelled
    external
    autoGameCreation
  }
`;

export default function GameSchedulesForm({ gameSchedules }) {
  const orgId = gameSchedules?.[0]?.org?.id;
  const originalLeagueId = gameSchedules?.[0]?.league?.id;
  const [selectedLeagueId, setSelectedLeagueId] = useState(originalLeagueId);

  const sanitizeObject = (obj, typename) => {
    if (!obj) return null;
    const clean = {
      id: obj.id,
      typename: typename,
    };

    Object.defineProperty(clean, "__typename", {
      value: typename,
      enumerable: false,
      writable: true,
    });

    return clean;
  };

  const LeagueInputWrapper = ({ onLeagueSelect, ...props }) => {
    return (
      <ReadminObjectInput
        {...props}
        optionsTypename="League"
        onChange={(newValue) => {
          if (onLeagueSelect) {
            onLeagueSelect(newValue);
          }
          if (props.onChange) {
            props.onChange(sanitizeObject(newValue, "League"));
          }
        }}
      />
    );
  };

  const [data] = useData(
    gql`
      query Org($orgId: ID!) {
        org(id: $orgId) {
          id
          defaultGameDurationSeconds
        }
      }
    `,
    { orgId },
    { skip: !orgId },
  );
  const orgDefaultDuration = data?.org?.defaultGameDurationSeconds || 600;

  const [leagueData, { loading: leagueLoading }] = useData(
    GET_LEAGUE_TEAMS,
    { id: selectedLeagueId },
    { skip: !selectedLeagueId },
  );

  const availableTeams = leagueData?.league?.teams;

  const isLeagueChanged = selectedLeagueId !== originalLeagueId;

  const homeTeamDefaults = isLeagueChanged ? gameSchedules?.map(() => null) : gameSchedules?.map((g) => g.homeTeam);

  const awayTeamDefaults = isLeagueChanged ? gameSchedules?.map(() => null) : gameSchedules?.map((g) => g.awayTeam);

  const mappedSchedules = gameSchedules?.map((gameSchedule) => ({
    cancelled: false,
    durationSeconds: orgDefaultDuration,
    ...gameSchedule,
    court: sanitizeObject(gameSchedule.court, "Court"),
    league: sanitizeObject(gameSchedule.league, "League"),
    homeTeam: sanitizeObject(gameSchedule.homeTeam, "Team"),
    awayTeam: sanitizeObject(gameSchedule.awayTeam, "Team"),
  }));

  return (
    <>
      <FormText uiStringKey="readmin.game_schedule.description" />
      <MultiValuesField
        label="Court"
        name="court"
        disabled={gameSchedules?.some((g) => g.external)}
        defaultValues={mappedSchedules?.map((g) => g.court)}
        helperTextUiStringKey="readmin.game_schedule.fields.court"
        input={<ReadminObjectInput optionsTypename="Court" />}
        required
      />

      <MultiValuesField
        label="League"
        name="league"
        disabled={gameSchedules?.some((g) => g.external)}
        helperTextUiStringKey="readmin.game_schedule.fields.league"
        defaultValues={mappedSchedules?.map((g) => g.league)}
        required
        input={<LeagueInputWrapper onLeagueSelect={(l) => setSelectedLeagueId(l?.id)} />}
      />

      <MultiValuesField
        key={`home-${selectedLeagueId}`}
        label="Home Team"
        name="homeTeam"
        disabled={gameSchedules?.some((g) => g.external) || leagueLoading || !selectedLeagueId}
        helperTextUiStringKey="readmin.game_schedule.fields.home_team"
        defaultValues={homeTeamDefaults}
        required
        input={<ReadminObjectInput optionsTypename="Team" options={availableTeams} />}
      />

      <MultiValuesField
        key={`away-${selectedLeagueId}`}
        label="Away Team"
        name="awayTeam"
        disabled={gameSchedules?.some((g) => g.external) || leagueLoading || !selectedLeagueId}
        helperTextUiStringKey="readmin.game_schedule.fields.away_team"
        defaultValues={awayTeamDefaults}
        required
        input={<ReadminObjectInput optionsTypename="Team" options={availableTeams} />}
      />

      <MultiValuesField
        label="Scheduled At"
        name="scheduledAt"
        disabled={gameSchedules?.some((g) => g.external)}
        helperTextUiStringKey="readmin.game_schedule.fields.scheduled_at"
        defaultValues={mappedSchedules?.map((g) => g.scheduledAt)}
        input={<DateTimeInput />}
        required
      />
      {gameSchedules?.some((g) => g.external) && (
        <Message
          type="warning"
          content="Changing the duration will shorten or extend the camera recording for this specific schedule."
        />
      )}
      <MultiValuesField
        label="Duration"
        name="durationSeconds"
        helperTextUiStringKey="readmin.game_schedule.fields.duration"
        defaultValues={mappedSchedules?.map((g) => g.durationSeconds)}
        input={<DurationInput unit="seconds" max={10800} />}
        required
      />
      <MultiValuesField
        label="Auto-Game Creation"
        name="autoGameCreation"
        disabled={gameSchedules?.some((g) => g.external)}
        helperTextUiStringKey="readmin.game_schedule.fields.auto_game_creation"
        defaultValues={mappedSchedules?.map((g) => g.autoGameCreation)}
        input={<SwitchInput />}
      />
      {gameSchedules?.some((g) => g.external) && (
        <Message
          type="warning"
          content={
            <>
              Cancelling the schedule here only affects Glory League.
              <br />
              It does not cancel the schedule in the competition management system.
            </>
          }
        />
      )}
      <MultiValuesField
        label="Cancelled"
        name="cancelled"
        helperTextUiStringKey="readmin.game_schedule.fields.cancelled"
        defaultValues={mappedSchedules?.map((g) => g.cancelled)}
        input={<SwitchInput />}
      />
    </>
  );
}
